import {
  useIsS,
  useIsXS,
  WSElement,
  WSGrid,
  WSModal
} from "@wingspanhq/fe-component-library";
import classNames from "classnames";
import React, { useEffect, useMemo, useRef } from "react";

import { useBrowserPageTitle } from "../../hooks/useBrowserPageTitle";
import { useLocationStateStorage } from "../../hooks/useLocationStateStorage";

import { ModalNavigation } from "./ModalNavigation";
import { ModalStepper } from "./ModalStepper";
import styles from "./index.module.scss";
import {
  ModalFlowInternalState,
  ModalFlowProps,
  ModalFlowStepKey,
  StepComponentProps
} from "./types";

export function ModalFlow<Props extends {}>(props: ModalFlowProps<Props>) {
  const {
    defaultStatuses,
    confirmExitModal,
    onClose,
    onFinish,
    sideSteps,
    steps,
    title
  } = props;

  useBrowserPageTitle(title);

  const isXS = useIsXS();
  const isS = useIsS();

  const [modalState, setModalState, resetModalState] = useLocationStateStorage<
    ModalFlowInternalState<Props>
  >(props.historyStateKey, {
    flow: {
      stepKey: steps[0].key,
      subStepKey: steps[0]?.steps?.[0].key
    },
    ...props.initialState
  });

  const {
    flow: { stepKey, subStepKey, sideStepKey },
    state
  } = modalState;

  const sideStep = sideSteps?.find(step => step.key === sideStepKey);

  const stepIndex = Math.max(
    0,
    steps.findIndex(step => step.key === stepKey)
  );
  const currentStep = steps[stepIndex]!;
  const subStepIndex = Math.max(
    0,
    currentStep?.steps?.findIndex(step => step.key === subStepKey) ?? 0
  );
  const currentSubStep = currentStep?.steps?.[subStepIndex];
  const activeTitle = currentStep?.title || title || "";

  const activeStep = sideStep || currentSubStep || currentStep;

  const resetModalStateRef = useRef(resetModalState);
  resetModalStateRef.current = resetModalState;

  useEffect(
    () => () => {
      resetModalStateRef.current();
    },
    []
  );

  const componentProps = useMemo(() => {
    const onExit = async () => {
      if (activeStep.confirmExit && confirmExitModal) {
        const confirm = await confirmExitModal.open();

        if (confirm) {
          onFinish?.("cancel");
          onClose();
        }
      } else {
        onFinish?.("cancel");
        onClose();
      }
    };

    const handleSuccess = () => {
      onFinish?.("success");
      onClose();
    };

    const componentProps: StepComponentProps<Props> = {
      goToSideStep(key: ModalFlowStepKey) {
        setModalState(prevState => ({
          ...prevState,
          flow: {
            ...prevState.flow,
            sideStepKey: key
          }
        }));
      },
      goToStep(stepKey: ModalFlowStepKey, subStepKey?: ModalFlowStepKey) {
        setModalState(prevState => ({
          ...prevState,
          flow: {
            ...prevState.flow,
            stepKey,
            subStepKey
          }
        }));
      },
      onBack() {
        if (sideStep) {
          setModalState(prevState => ({
            ...prevState,
            flow: {
              ...prevState.flow,
              sideStepKey: undefined
            }
          }));
          return;
        }

        if (subStepIndex > 0) {
          setModalState(prevState => ({
            ...prevState,
            flow: {
              ...prevState.flow,
              subStepKey: currentStep.steps?.[subStepIndex - 1]?.key
            }
          }));
        } else if (stepIndex > 0) {
          const prevStep = steps[stepIndex - 1];

          setModalState(prevState => ({
            ...prevState,
            flow: {
              ...prevState.flow,
              stepKey: steps[stepIndex - 1]?.key!,
              subStepKey: prevStep.steps?.[prevStep.steps!.length - 1]?.key
            }
          }));
        } else {
          void onExit();
        }
      },
      onClose() {
        void onExit();
      },
      onNext() {
        if (sideStep) {
          setModalState(prevState => ({
            ...prevState,
            flow: {
              ...prevState.flow,
              sideStepKey: undefined
            }
          }));
          return;
        }

        if (
          currentStep.steps?.length &&
          subStepIndex < currentStep.steps.length - 1
        ) {
          setModalState(prevState => ({
            ...prevState,
            flow: {
              ...prevState.flow,
              subStepKey: currentStep.steps[subStepIndex + 1]?.key
            }
          }));
        } else if (stepIndex < steps.length - 1) {
          setModalState(prevState => ({
            ...prevState,
            flow: {
              ...prevState.flow,
              stepKey: steps[stepIndex + 1].key,
              subStepKey: steps[stepIndex + 1].steps?.[0]?.key
            }
          }));
        } else {
          handleSuccess();
        }
      },
      onSuccess() {
        handleSuccess();
      },
      setState(newState: Partial<ModalFlowInternalState<Props>["state"]>) {
        setModalState(prevState => ({
          ...prevState,
          state: {
            ...prevState.state,
            ...newState
          }
        }));
      },
      state
    };

    return componentProps;
  }, [
    activeStep.confirmExit,
    confirmExitModal,
    currentStep,
    onClose,
    onFinish,
    setModalState,
    sideStep,
    state,
    stepIndex,
    steps,
    subStepIndex
  ]);

  const Component = sideStep
    ? sideStep.component
    : currentSubStep
      ? currentSubStep.component!
      : currentStep.component
        ? currentStep.component
        : () => null;

  return (
    <WSModal
      key="modal-flow"
      blockClose={activeStep.confirmExit}
      colorBackground="gray100"
      fullScreen
      fullScreenOnMobile
      title={activeTitle}
      titleAlign="center"
      onClose={() => {
        onFinish?.("cancel");
        onClose();
      }}
    >
      <WSElement className={classNames(styles.content, styles.twoColumn)}>
        <WSGrid>
          <WSGrid.Item span={{ m: "3" }}>
            {isXS || isS ? (
              <ModalStepper
                currentStep={currentStep}
                currentSubStep={currentSubStep}
                defaultStatuses={defaultStatuses}
                mb="L"
                steps={steps}
              />
            ) : (
              <ModalNavigation
                currentStep={currentStep}
                currentSubStep={currentSubStep}
                defaultStatuses={defaultStatuses}
                steps={steps}
              />
            )}
          </WSGrid.Item>
          <WSGrid.Item span={{ m: "9" }}>
            <Component {...componentProps} />
          </WSGrid.Item>
        </WSGrid>
      </WSElement>
    </WSModal>
  );
}
